import { useMemo } from 'react';
import type { GraphNode, GraphEdge } from '../types';

// =============================================================================
// Simple SVG-based Hive Graph Visualization
// =============================================================================

interface HiveGraphViewProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

interface PositionedNode extends GraphNode {
  x: number;
  y: number;
}

/**
 * Layout nodes in a layered graph. Entry node at top, then BFS layers.
 */
function layoutNodes(nodes: GraphNode[], edges: GraphEdge[]): PositionedNode[] {
  if (nodes.length === 0) return [];

  const NODE_W = 180;
  const NODE_H = 60;
  const H_GAP = 60;
  const V_GAP = 100;

  // Build adjacency from source → targets
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    const list = adj.get(e.source) ?? [];
    list.push(e.target);
    adj.set(e.source, list);
  }

  // BFS from entry node(s)
  const entry = nodes.find((n) => n.is_entry);
  const startIds = entry ? [entry.id] : [nodes[0].id];

  const visited = new Set<string>();
  const layers: string[][] = [];
  let frontier = [...startIds];

  while (frontier.length > 0) {
    const layer: string[] = [];
    const nextFrontier: string[] = [];
    for (const id of frontier) {
      if (visited.has(id)) continue;
      visited.add(id);
      layer.push(id);
      const neighbors = adj.get(id) ?? [];
      for (const n of neighbors) {
        if (!visited.has(n)) nextFrontier.push(n);
      }
    }
    if (layer.length > 0) layers.push(layer);
    frontier = nextFrontier;
  }

  // Any unvisited nodes get their own layer
  const remaining = nodes.filter((n) => !visited.has(n.id)).map((n) => n.id);
  if (remaining.length > 0) layers.push(remaining);

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const positioned: PositionedNode[] = [];

  for (let li = 0; li < layers.length; li++) {
    const layer = layers[li];
    const totalWidth = layer.length * NODE_W + (layer.length - 1) * H_GAP;
    const startX = -totalWidth / 2 + NODE_W / 2;

    for (let ni = 0; ni < layer.length; ni++) {
      const node = nodeMap.get(layer[ni]);
      if (!node) continue;
      positioned.push({
        ...node,
        x: startX + ni * (NODE_W + H_GAP),
        y: li * (NODE_H + V_GAP),
      });
    }
  }

  return positioned;
}

export function HiveGraphView({ nodes, edges }: HiveGraphViewProps) {
  const positioned = useMemo(() => layoutNodes(nodes, edges), [nodes, edges]);

  const nodePositionMap = useMemo(
    () => new Map(positioned.map((n) => [n.id, { x: n.x, y: n.y }])),
    [positioned]
  );

  // Compute SVG bounds
  const padding = 100;
  const nodeW = 180;
  const nodeH = 60;

  const minX = Math.min(...positioned.map((n) => n.x)) - nodeW / 2 - padding;
  const maxX = Math.max(...positioned.map((n) => n.x)) + nodeW / 2 + padding;
  const minY = Math.min(...positioned.map((n) => n.y)) - padding;
  const maxY = Math.max(...positioned.map((n) => n.y)) + nodeH + padding;

  const svgWidth = maxX - minX;
  const svgHeight = maxY - minY;

  return (
    <div className="overflow-auto">
      <svg
        viewBox={`${minX} ${minY} ${svgWidth} ${svgHeight}`}
        className="w-full"
        style={{ minHeight: Math.min(svgHeight, 600) }}
      >
        <defs>
          <marker
            id="arrow"
            viewBox="0 0 10 10"
            refX="10"
            refY="5"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" className="fill-gray-400 dark:fill-gray-500" />
          </marker>
          <marker
            id="arrow-transfer"
            viewBox="0 0 10 10"
            refX="10"
            refY="5"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" className="fill-blue-400" />
          </marker>
        </defs>

        {/* Edges */}
        {edges.map((edge, i) => {
          const src = nodePositionMap.get(edge.source);
          const tgt = nodePositionMap.get(edge.target);
          if (!src || !tgt) return null;

          const isTransfer = edge.type === 'transfer';
          const sx = src.x;
          const sy = src.y + nodeH;
          const tx = tgt.x;
          const ty = tgt.y;

          // Slight curve
          const mx = (sx + tx) / 2;
          const my = (sy + ty) / 2 - 20;

          return (
            <g key={`edge-${i}`}>
              <path
                d={`M ${sx} ${sy} Q ${mx} ${my} ${tx} ${ty}`}
                fill="none"
                className={isTransfer ? 'stroke-blue-400' : 'stroke-gray-400 dark:stroke-gray-500'}
                strokeWidth={2}
                markerEnd={isTransfer ? 'url(#arrow-transfer)' : 'url(#arrow)'}
              />
              {edge.description && (
                <text
                  x={mx}
                  y={my - 8}
                  textAnchor="middle"
                  className="fill-gray-500 dark:fill-gray-400 text-[10px]"
                >
                  {edge.description}
                </text>
              )}
              {/* Mode badge */}
              {edge.mode && (
                <text
                  x={mx}
                  y={my + 8}
                  textAnchor="middle"
                  className="fill-gray-400 dark:fill-gray-500 text-[9px] italic"
                >
                  {edge.mode}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {positioned.map((node) => (
          <g key={node.id} transform={`translate(${node.x - nodeW / 2}, ${node.y})`}>
            <rect
              width={nodeW}
              height={nodeH}
              rx={8}
              className={
                node.is_entry
                  ? 'fill-purple-50 dark:fill-purple-900/30 stroke-purple-400 dark:stroke-purple-500'
                  : 'fill-white dark:fill-gray-800 stroke-gray-300 dark:stroke-gray-600'
              }
              strokeWidth={node.is_entry ? 2 : 1}
            />
            {/* Icon placeholder */}
            <circle
              cx={24}
              cy={nodeH / 2}
              r={12}
              className={
                node.is_entry
                  ? 'fill-purple-100 dark:fill-purple-800'
                  : 'fill-blue-100 dark:fill-blue-900'
              }
            />
            <text
              x={24}
              y={nodeH / 2 + 4}
              textAnchor="middle"
              className={
                node.is_entry
                  ? 'fill-purple-600 dark:fill-purple-400 text-[10px]'
                  : 'fill-blue-600 dark:fill-blue-400 text-[10px]'
              }
            >
              ★
            </text>
            {/* Name */}
            <text
              x={44}
              y={nodeH / 2 - 4}
              className="fill-gray-900 dark:fill-gray-100 text-[12px] font-medium"
            >
              {node.name.length > 16 ? node.name.slice(0, 14) + '…' : node.name}
            </text>
            {/* Status */}
            <text
              x={44}
              y={nodeH / 2 + 12}
              className="fill-gray-500 dark:fill-gray-400 text-[10px]"
            >
              {node.is_entry ? 'entry' : node.status}
            </text>
            {/* Handoff count badge */}
            {node.handoff_count > 0 && (
              <>
                <circle
                  cx={nodeW - 18}
                  cy={12}
                  r={10}
                  className="fill-gray-100 dark:fill-gray-700 stroke-gray-300 dark:stroke-gray-600"
                  strokeWidth={1}
                />
                <text
                  x={nodeW - 18}
                  y={16}
                  textAnchor="middle"
                  className="fill-gray-600 dark:fill-gray-300 text-[10px] font-medium"
                >
                  {node.handoff_count}
                </text>
              </>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}
